import { formatDay } from "utils/helpers";
import { useTranslation } from "react-i18next";

interface SingleRegisterDirectiveProps {
    personImg: any;
    directive: any;
    isActive?: boolean;
    onClick?: () => void;
}

export default function SingleRegisterDirective({
    personImg,
    directive,
    isActive,
    onClick
}: SingleRegisterDirectiveProps) {
    const { t } = useTranslation();
    return (
        <div
            onClick={onClick}
            className={`flex flex-col rounded-lg shadow-md p-6 mb-4 cursor-pointer transition-all duration-200
                ${isActive ? "bg-gray-50 border-l-4 border-gray-400" : "bg-white hover:bg-gray-50"}`}
        >
            <div className="flex gap-1 font-regular text-xs text-[#747171] mb-3">
                <p>{formatDay(directive.request_created_date || directive.updated_at || directive.created_at)?.weekday || ""},</p>
                <p>{formatDay(directive.request_created_date || directive.updated_at || directive.created_at)?.rest || ""}</p>
            </div>

            <div>
                <div className="flex items-center mb-3 text-base font-semibold text-[#212121] gap-2">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        className="flex-shrink-0"
                    >
                        <path
                            d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zM14 8V2l6 6h-6z"
                            fill={isActive ? "#6b7280" : "#212121"}
                        />
                    </svg>
                    <p className="font-bold text-lg">
                        {t("RegisterDirective")}
                        <span className="text-gray-600 ml-1">{directive.registry_number || directive.application_number || "-"}</span>
                    </p>
                </div>

                {/* Organization and Explanatory Notes */}
                <div className="mb-4">
                    <p className="font-light text-sm text-[#212121] leading-relaxed line-clamp-2">
                        {directive.organization_name || t("Stakeholder")}
                    </p>
                    {directive.explanatory_notes && (
                        <div className="space-y-2 max-h-20 overflow-hidden mt-2">
                            {directive.explanatory_notes.split('\n').map((paragraph: string, index: number) => (
                                paragraph.trim() !== '' && (
                                    <p
                                        key={index}
                                        className="font-light text-sm text-gray-500 leading-relaxed line-clamp-2"
                                    >
                                        {paragraph}
                                    </p>
                                )
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex gap-4 mb-4">
                    <svg
                        className="w-8 h-fit"
                        xmlns="http://www.w3.org/2000/svg"
                        width="32"
                        height="32"
                        viewBox="0 0 20 20"
                        fill="none"
                    >
                        <path
                            d="M11.5326 20H8.00258C7.75258 20 7.54258 19.82 7.50258 19.58L7.13258 16.93C6.50258 16.68 5.96258 16.34 5.44258 15.94L2.95258 16.95C2.73258 17.03 2.46258 16.95 2.34258 16.73L0.342578 13.27C0.281374 13.167 0.259797 13.0452 0.281883 12.9274C0.303969 12.8096 0.368205 12.7039 0.462578 12.63L2.57258 10.97C2.53258 10.65 2.50258 10.33 2.50258 10C2.50258 9.67 2.53258 9.34 2.57258 9L0.462578 7.37C0.368205 7.29613 0.303969 7.19042 0.281883 7.07263C0.259797 6.95484 0.281374 6.83304 0.342578 6.73L2.34258 3.27C2.46258 3.05 2.73258 2.96 2.95258 3.05L5.44258 4.05C5.96258 3.66 6.50258 3.32 7.13258 3.07L7.50258 0.42C7.54258 0.18 7.75258 0 8.00258 0H12.0026C12.2526 0 12.4626 0.18 12.5026 0.42L12.8726 3.07C13.5026 3.32 14.0426 3.66 14.5626 4.05L17.0526 3.05C17.2726 2.96 17.5426 3.05 17.6626 3.27L19.6626 6.73C19.7826 6.95 19.7326 7.22 19.5426 7.37L17.4326 9C17.4726 9.34 17.5026 9.67 17.5026 10V10.19C17.0026 10.07 16.5026 10 16.0026 10C15.0826 10 14.2226 10.21 13.4426 10.58C13.4726 10.39 13.5026 10.2 13.5026 10C13.5026 8.07 11.9326 6.5 10.0026 6.5C8.07258 6.5 6.50258 8.07 6.50258 10C6.50258 11.93 8.07258 13.5 10.0026 13.5C10.2026 13.5 10.3926 13.47 10.5826 13.44C10.0751 14.505 9.89444 15.6964 10.0635 16.864C10.2326 18.0315 10.7438 19.1228 11.5326 20ZM14.0026 13V19L19.0026 16L14.0026 13Z"
                            fill={isActive ? "#d1d5db" : "#D4D4D4"}
                        />
                    </svg>
                </div>

                <div className="flex items-center">
                    <div className="flex items-center flex-1">
                        <div className="relative mr-3">
                            <img
                                src={personImg}
                                alt="Avatar"
                                width={40}
                                height={40}
                                className="object-cover rounded-full h-fit"
                            />
                        </div>

                        <div>
                            <p className="text-[#0A1D39] font-semibold text-base">
                                {directive.requested_by_user?.full_name || "Official Registry"}
                            </p>
                            <div className="flex items-center gap-2">
                                <p className="text-[#9D0303] font-medium text-xs">{t("RegisterDirective")}</p>
                            </div>
                        </div>
                    </div>

                    <button className={`rounded-md px-6 py-2 h-fit font-medium text-sm ${
                        isActive
                            ? "bg-gray-400 text-white"
                            : "bg-green-600 text-white hover:bg-green-700"
                    }`}>
                        {t("Published")}
                    </button>
                </div>
            </div>
        </div>
    );
}
